/**
 * Excel and PDF export, done in the browser.
 *
 * Keeping this client-side means the Worker never has to hold a whole month of
 * rows in memory to build a file, and the download starts immediately.
 */

import { dataCurta, euros, horas, km, mesPorExtenso, numero, percentagem } from './formatos';
import type { DadosDashboard, Funcionario, LinhaFolha, Registo, Totais } from './tipos';

/*
 * xlsx and jspdf are ~370 kB together and are only needed when someone clicks
 * export, so they are loaded on demand rather than in the initial bundle.
 */
const carregarXlsx = () => import('xlsx');
const carregarPdf = async () => {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'), import('jspdf-autotable'),
  ]);
  return { jsPDF, autoTable };
};

function nomeFicheiro(base: string, sufixo: string) {
  const carimbo = new Date().toISOString().slice(0, 10);
  return `${base}-${sufixo}-${carimbo}`.replace(/[^a-zA-Z0-9-]/g, '-');
}

// -- shared page furniture ---------------------------------------------------

type Doc = InstanceType<typeof import('jspdf').default>;

const A4 = { orientation: 'portrait' as const, unit: 'mm' as const, format: 'a4' as const };

// snip.work brand tokens. TINTA reads as near-black, PAPEL as the off-white
// the app is built on, MARCA is the accent darkened to clear 3:1 on paper.
const TINTA: [number, number, number] = [21, 18, 15];
const PAPEL: [number, number, number] = [242, 239, 231];
const CINZA: [number, number, number] = [167, 156, 139];
const MARCA: [number, number, number] = [214, 58, 18];
const GRELHA: [number, number, number] = [233, 227, 214];

const MARGEM = 14;
const LARGURA_UTIL = 210 - MARGEM * 2;

/**
 * The footer every generated document carries: what it is on the left, where
 * it came from, and the page count on the right.
 *
 * Stamped after the content is laid out rather than while drawing it, because
 * the page count is not known until then — a footer written mid-flow can only
 * claim to be page 1 of 1 and be wrong the moment the content spills.
 */
function rodape(doc: Doc, nome: string) {
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const paginas = doc.getNumberOfPages();

  for (let i = 1; i <= paginas; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...CINZA);
    doc.text(`${nome} · snip.work`, MARGEM, altura - 10);
    doc.text(`Página ${i} de ${paginas}`, largura - MARGEM, altura - 10, { align: 'right' });
  }
  doc.setTextColor(0);
}

const COLUNAS_REGISTO = [
  'Funcionário', 'Função', 'Data', 'Início', 'Fim', 'Pausa (min)',
  'Horas', 'Horas extra', 'Local', 'Km', 'Nº Serviço', 'Custo', 'Notas',
] as const;

function linhaRegisto(r: Registo) {
  return [
    r.funcionario_nome,
    r.funcao_nome ?? r.funcao,
    dataCurta(r.data, true),
    r.hora_inicio,
    r.hora_fim,
    r.pausa_minutos,
    r.total_horas,
    r.horas_extra,
    r.local_nome ?? '',
    r.km,
    r.numero_servico ?? '',
    r.custo_total ?? '',
    r.notas ?? '',
  ];
}

export async function exportarRegistosExcel(registos: Registo[], totais: Totais, descricao: string) {
  const XLSX = await carregarXlsx();
  const dados = [
    COLUNAS_REGISTO as unknown as string[],
    ...registos.map(linhaRegisto),
    [],
    ['TOTAL', '', '', '', '', '', totais.total_horas, totais.horas_extra ?? '', '', totais.total_km, '', totais.custo_total ?? '', ''],
  ];

  const folha = XLSX.utils.aoa_to_sheet(dados);
  folha['!cols'] = [
    { wch: 24 }, { wch: 18 }, { wch: 11 }, { wch: 8 }, { wch: 8 }, { wch: 11 },
    { wch: 9 }, { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 13 }, { wch: 11 }, { wch: 30 },
  ];
  folha['!autofilter'] = { ref: `A1:M${registos.length + 1}` };
  folha['!freeze'] = { xSplit: 0, ySplit: 1 };

  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, folha, 'Registo de Horas');
  XLSX.writeFile(livro, `${nomeFicheiro('registo-horas', descricao)}.xlsx`);
}

export async function exportarRegistosPdf(registos: Registo[], totais: Totais, descricao: string) {
  const { jsPDF, autoTable } = await carregarPdf();
  // Landscape: thirteen columns do not fit portrait without shrinking the text
  // past readability.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  doc.setFontSize(14);
  doc.text('Registo de Horas', 14, 15);
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(descricao, 14, 21);
  doc.text(`Gerado a ${new Date().toLocaleString('pt-PT')}`, 14, 26);

  autoTable(doc, {
    startY: 31,
    head: [COLUNAS_REGISTO as unknown as string[]],
    body: registos.map((r) => linhaRegisto(r).map((v, i) =>
      i === 11 && v !== '' ? euros(Number(v)) : String(v))),
    foot: [[
      'TOTAL', '', '', '', '', '',
      String(totais.total_horas), String(totais.horas_extra ?? ''), '',
      String(totais.total_km), '', totais.custo_total === null ? '' : euros(totais.custo_total), '',
    ]],
    styles: { fontSize: 7.5, cellPadding: 1.6 },
    headStyles: { fillColor: [55, 48, 163], textColor: 255, fontSize: 7.5 },
    footStyles: { fillColor: [241, 245, 249], textColor: 20, fontStyle: 'bold' },
    columnStyles: {
      6: { halign: 'right' }, 7: { halign: 'right' },
      9: { halign: 'right' }, 11: { halign: 'right' },
    },
  });

  rodape(doc, 'Registo de Horas');
  doc.save(`${nomeFicheiro('registo-horas', descricao)}.pdf`);
}

/*
 * Folha de Ponto — the flat, row-per-shift sheet. This is the one that gets
 * printed and checked against the paper rota, so its column order mirrors the
 * screen exactly rather than the richer export above.
 */
const COLUNAS_PONTO = [
  'Data', 'Início', 'Fim', 'Total', 'Local', 'Km', 'Nº Serviço',
  'Funcionário', 'Função', 'Fornecedor', '€/h', 'A Pagar',
] as const;

const linhaPonto = (r: Registo) => [
  dataCurta(r.data, true), r.hora_inicio, r.hora_fim, r.total_horas,
  r.local_nome ?? '', r.km, r.numero_servico ?? '',
  r.funcionario_nome, r.funcao_nome ?? r.funcao, r.empresa_nome ?? '',
  r.custo_hora_efetivo ?? '', r.custo_total ?? '',
];

/**
 * The cost summary that closes the sheet: for each person, the multiplication
 * that produced their pay.
 *
 * Grouped by rate as well as by person, because a rate belongs to the shift —
 * someone who worked one job through an agency at 11.50 and the next directly
 * at 9.00 gets two lines. Merged into one, `horas × €/h` would not equal the
 * value beside it, and a document whose own arithmetic does not check out is
 * worse than no document.
 */
interface LinhaCusto {
  funcionario: string;
  empresa: string;
  horas: number;
  taxa: number | null;
  valorHoras: number;
  km: number;
  valorKm: number;
  total: number;
}

function resumirCustos(registos: Registo[]): LinhaCusto[] {
  const mapa = new Map<string, LinhaCusto>();
  for (const r of registos) {
    const taxa = r.custo_hora_efetivo ?? null;
    const chave = `${r.funcionario_nome}|${r.empresa_nome ?? ''}|${taxa ?? 'sem'}`;
    const linha = mapa.get(chave) ?? {
      funcionario: r.funcionario_nome,
      empresa: r.empresa_nome ?? '—',
      horas: 0, taxa, valorHoras: 0, km: 0, valorKm: 0, total: 0,
    };
    linha.horas += r.total_horas;
    linha.valorHoras += r.valor_horas ?? 0;
    linha.km += r.km;
    linha.valorKm += r.valor_km_total ?? 0;
    linha.total += r.custo_total ?? 0;
    mapa.set(chave, linha);
  }
  const arred = (n: number) => Math.round(n * 100) / 100;
  return [...mapa.values()]
    .map((l) => ({
      ...l,
      horas: arred(l.horas), valorHoras: arred(l.valorHoras),
      km: arred(l.km), valorKm: arred(l.valorKm), total: arred(l.total),
    }))
    .sort((a, b) => b.total - a.total);
}

interface LinhaFornecedor {
  fornecedor: string;
  funcionarios: number;
  horas: number;
  valorHoras: number;
  km: number;
  valorKm: number;
  total: number;
}

/**
 * The same month seen one level up: what each supplier is owed.
 *
 * This is the figure an agency invoice is checked against, and it is not the
 * per-person table with a different sort — a supplier bills for its people
 * together, and someone who worked for two suppliers belongs to both.
 */
function resumirFornecedores(registos: Registo[]): LinhaFornecedor[] {
  const mapa = new Map<string, LinhaFornecedor & { pessoas: Set<string> }>();
  for (const r of registos) {
    const nome = r.empresa_nome ?? 'Sem fornecedor';
    const linha = mapa.get(nome) ?? {
      fornecedor: nome, funcionarios: 0, horas: 0, valorHoras: 0,
      km: 0, valorKm: 0, total: 0, pessoas: new Set<string>(),
    };
    linha.pessoas.add(r.funcionario_id);
    linha.horas += r.total_horas;
    linha.valorHoras += r.valor_horas ?? 0;
    linha.km += r.km;
    linha.valorKm += r.valor_km_total ?? 0;
    linha.total += r.custo_total ?? 0;
    mapa.set(nome, linha);
  }
  const arred = (n: number) => Math.round(n * 100) / 100;
  return [...mapa.values()]
    .map(({ pessoas, ...l }) => ({
      ...l,
      funcionarios: pessoas.size,
      horas: arred(l.horas), valorHoras: arred(l.valorHoras),
      km: arred(l.km), valorKm: arred(l.valorKm), total: arred(l.total),
    }))
    .sort((a, b) => b.total - a.total);
}

/** The per-km rate actually applied, or a range when it varied. */
function taxaKm(registos: Registo[]): string {
  const taxas = [...new Set(
    registos.filter((r) => r.km > 0).map((r) => r.valor_km_efetivo ?? 0),
  )].sort((a, b) => a - b);
  if (!taxas.length) return '—';
  return taxas.length === 1
    ? euros(taxas[0])
    : `${euros(taxas[0])} – ${euros(taxas[taxas.length - 1])}`;
}

export async function exportarPontoExcel(registos: Registo[], totais: Totais, descricao: string) {
  const XLSX = await carregarXlsx();
  const custos = resumirCustos(registos);
  const fornecedores = resumirFornecedores(registos);

  /*
   * Units live in the cell's number format, not in its text.
   *
   * A cell reading "12.50h" as a string cannot be summed, filtered or charted,
   * which defeats the point of exporting a spreadsheet at all. Written as the
   * number 12.5 with a format of 0.00"h", it displays as 12.50h and still adds
   * up. The same for km and for euros.
   */
  const HORAS = '0.00"h"';
  const KM = '0" km"';
  const MOEDA = '#,##0.00\\ "€"';

  type Celula = { v?: unknown; z?: string };
  /** Apply a format to whole columns, and to individually named cells. */
  const formatar = (
    folha: Record<string, Celula>,
    porColuna: Record<string, string>,
    porCelula: Record<string, string> = {},
  ) => {
    for (const ref of Object.keys(folha)) {
      if (ref[0] === '!') continue;
      if (typeof folha[ref].v !== 'number') continue;
      const formato = porCelula[ref] ?? porColuna[ref.replace(/\d+/g, '')];
      if (formato) folha[ref].z = formato;
    }
  };

  /** The month's arithmetic, spelled out. Closes every summary sheet. */
  const fecho = (): (string | number)[][] => [
    [],
    [],
    ['Total de horas', totais.total_horas],
    ['Valor das horas', totais.valor_horas ?? ''],
    ['Total de km', totais.total_km],
    [`Valor por km (${taxaKm(registos)}/km)`, totais.valor_km ?? ''],
    ['TOTAL A PAGAR', totais.custo_total ?? ''],
  ];
  /** Formats for that closing block, which mixes hours, km and euros in one column. */
  const formatosFecho = (primeiraLinha: number) => ({
    [`B${primeiraLinha}`]: HORAS,
    [`B${primeiraLinha + 1}`]: MOEDA,
    [`B${primeiraLinha + 2}`]: KM,
    [`B${primeiraLinha + 3}`]: MOEDA,
    [`B${primeiraLinha + 4}`]: MOEDA,
  });

  // -- 1 · Folha de Ponto: every shift ---------------------------------------
  const ponto = XLSX.utils.aoa_to_sheet([
    [`Folha de Ponto Mensal — ${descricao}`],
    [],
    COLUNAS_PONTO as unknown as string[],
    ...registos.map(linhaPonto),
    [],
    ['TOTAL', '', '', totais.total_horas, '', totais.total_km, '', '', '', '', '',
      totais.custo_total ?? ''],
  ]);
  ponto['!cols'] = [
    { wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 18 },
    { wch: 10 }, { wch: 14 }, { wch: 30 }, { wch: 18 }, { wch: 18 },
    { wch: 10 }, { wch: 12 },
  ];
  ponto['!freeze'] = { xSplit: 0, ySplit: 3 };
  formatar(ponto as never, { D: HORAS, F: KM, K: MOEDA, L: MOEDA });

  // -- 2 · Por Funcionário: what each person is owed --------------------------
  const porFuncionario = XLSX.utils.aoa_to_sheet([
    [`Por Funcionário — ${descricao}`],
    [],
    ['Funcionário', 'Fornecedor', 'Horas', '€/h', 'Valor horas', 'Km', 'Valor km', 'Total'],
    ...custos.map((c) => [
      c.funcionario, c.empresa, c.horas, c.taxa ?? '', c.valorHoras,
      c.km, c.valorKm, c.total,
    ]),
    ['TOTAL', '', totais.total_horas, '', totais.valor_horas ?? '',
      totais.total_km, totais.valor_km ?? '', totais.custo_total ?? ''],
    ...fecho(),
  ]);
  porFuncionario['!cols'] = [
    { wch: 34 }, { wch: 18 }, { wch: 11 }, { wch: 10 },
    { wch: 13 }, { wch: 10 }, { wch: 12 }, { wch: 13 },
  ];
  porFuncionario['!freeze'] = { xSplit: 0, ySplit: 3 };
  formatar(
    porFuncionario as never,
    { C: HORAS, D: MOEDA, E: MOEDA, F: KM, G: MOEDA, H: MOEDA },
    formatosFecho(3 + custos.length + 1 + 2 + 1),
  );

  // -- 3 · Por Fornecedor: what each supplier is owed -------------------------
  //
  // The same month one level up. This is the figure an agency invoice is
  // checked against, and it is not the per-person sheet re-sorted: a supplier
  // bills for its people together, and someone who worked for two suppliers
  // counts once under each.
  const porFornecedor = XLSX.utils.aoa_to_sheet([
    [`Por Fornecedor — ${descricao}`],
    [],
    ['Fornecedor', 'Funcionários', 'Horas', 'Valor horas', 'Km', 'Valor km', 'Total'],
    ...fornecedores.map((f) => [
      f.fornecedor, f.funcionarios, f.horas, f.valorHoras, f.km, f.valorKm, f.total,
    ]),
    ['TOTAL', totais.n_funcionarios ?? '', totais.total_horas, totais.valor_horas ?? '',
      totais.total_km, totais.valor_km ?? '', totais.custo_total ?? ''],
    ...fecho(),
  ]);
  porFornecedor['!cols'] = [
    { wch: 26 }, { wch: 14 }, { wch: 11 }, { wch: 13 },
    { wch: 10 }, { wch: 12 }, { wch: 13 },
  ];
  porFornecedor['!freeze'] = { xSplit: 0, ySplit: 3 };
  formatar(
    porFornecedor as never,
    { C: HORAS, D: MOEDA, E: KM, F: MOEDA, G: MOEDA },
    formatosFecho(3 + fornecedores.length + 1 + 2 + 1),
  );

  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, ponto, 'Folha de Ponto');
  XLSX.utils.book_append_sheet(livro, porFuncionario, 'Por Funcionário');
  XLSX.utils.book_append_sheet(livro, porFornecedor, 'Por Fornecedor');
  XLSX.writeFile(livro, `${nomeFicheiro('folha-de-ponto', descricao)}.xlsx`);
}

export async function exportarPontoPdf(registos: Registo[], totais: Totais, descricao: string) {
  const { jsPDF, autoTable } = await carregarPdf();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const custos = resumirCustos(registos);
  const fornecedores = resumirFornecedores(registos);

  doc.setFontSize(14);
  doc.text('Folha de Ponto Mensal', 14, 15);
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(descricao, 14, 21);
  doc.text(`Gerado a ${new Date().toLocaleString('pt-PT')}`, 14, 26);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 31,
    head: [COLUNAS_PONTO as unknown as string[]],
    // Each column printed as the thing it measures: hours as hours, km as km,
    // money as money. A bare "12.5" next to a bare "128" reads as two of the
    // same kind of number, and they are not.
    body: registos.map((r) => linhaPonto(r).map((v, i) => {
      if (v === '' || v === null) return '';
      if (i === 3) return horas(Number(v));
      if (i === 5) return km(Number(v));
      if (i === 10 || i === 11) return euros(Number(v));
      return String(v);
    })),
    foot: [[
      'TOTAL', '', '', horas(totais.total_horas), '', km(totais.total_km),
      '', '', '', '', '', euros(totais.custo_total),
    ]],
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 8 },
    footStyles: { fillColor: PAPEL, textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: {
      3: { halign: 'right' }, 5: { halign: 'right' },
      10: { halign: 'right' }, 11: { halign: 'right', fontStyle: 'bold' },
    },
  });

  // The cost summary goes on its own page: it is what gets checked against an
  // invoice, and hunting for it at the bottom of a long timesheet is the
  // reason people rebuild it in a spreadsheet instead.
  doc.addPage();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Por Funcionário', 14, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(descricao, 14, 21);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 27,
    head: [['Funcionário', 'Fornecedor', 'Horas', '€/h', 'Valor horas', 'Km', 'Valor km', 'Total']],
    body: custos.map((c) => [
      c.funcionario, c.empresa, horas(c.horas),
      c.taxa === null ? '—' : `${euros(c.taxa)}/h`, euros(c.valorHoras),
      km(c.km), euros(c.valorKm), euros(c.total),
    ]),
    foot: [[
      'TOTAL', '', horas(totais.total_horas), '',
      euros(totais.valor_horas), km(totais.total_km),
      euros(totais.valor_km), euros(totais.custo_total),
    ]],
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 8 },
    footStyles: { fillColor: PAPEL, textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: {
      2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
      5: { halign: 'right' }, 6: { halign: 'right' },
      7: { halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: 14, right: 14 },
  });

  // -- por fornecedor: the same month one level up ---------------------------
  doc.addPage();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Por Fornecedor', 14, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(descricao, 14, 21);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 27,
    head: [['Fornecedor', 'Funcionários', 'Horas', 'Valor horas', 'Km', 'Valor km', 'Total']],
    body: fornecedores.map((f) => [
      f.fornecedor, String(f.funcionarios), horas(f.horas), euros(f.valorHoras),
      km(f.km), euros(f.valorKm), euros(f.total),
    ]),
    foot: [[
      'TOTAL', String(totais.n_funcionarios ?? ''), horas(totais.total_horas),
      euros(totais.valor_horas), km(totais.total_km),
      euros(totais.valor_km), euros(totais.custo_total),
    ]],
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 8 },
    footStyles: { fillColor: PAPEL, textColor: 20, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: {
      1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
      4: { halign: 'right' }, 5: { halign: 'right' },
      6: { halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: 14, right: 14, bottom: 18 },
  });

  // -- resumo de custos: the document's closing statement ---------------------
  //
  // On its own page, and not tucked under the fornecedor table. Each of the
  // three tables already foots its own TOTAL; printed immediately below one of
  // them, this block repeated those same figures and read as a second,
  // unexplained total for that table rather than as the month's bottom line.
  doc.addPage();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Resumo de Custos', 14, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(descricao, 14, 21);
  doc.setFontSize(7.5);
  doc.text(
    'As três tabelas anteriores — folha de ponto, por funcionário e por fornecedor — '
    + 'somam todas este mesmo total, cada uma por um caminho diferente.',
    14, 27,
  );
  doc.setTextColor(0);

  blocoTotais(doc, 40, [
    ['Registos', numero(totais.n_registos)],
    ['Funcionários', numero(totais.n_funcionarios)],
    ['Fornecedores', numero(fornecedores.length)],
    ['', ''],
    ['Total de horas', horas(totais.total_horas)],
    [`Valor das horas`, euros(totais.valor_horas)],
    ['Total de km', km(totais.total_km)],
    [`Valor por km (${taxaKm(registos)}/km)`, euros(totais.valor_km)],
  ], ['Total a pagar', euros(totais.custo_total)]);

  rodape(doc, 'Folha de Ponto Mensal');
  doc.save(`${nomeFicheiro('folha-de-ponto', descricao)}.pdf`);
}

/*
 * The employee register. Payroll asks for this as a spreadsheet every time
 * someone joins or a card is renewed, so it carries the full record — NIF and
 * NIB included — rather than the subset the list happens to show.
 */
const COLUNAS_FUNCIONARIO = [
  'Nome', 'Fornecedor', 'Função', 'Nascimento', 'Email', 'Telefone',
  'Cartão de ID', 'Validade', 'Estado do cartão', 'NIF', 'NIB',
  'Admissão', 'Valor/hora', 'Estado', 'Observações',
] as const;

const ESTADO_CARTAO: Record<string, string> = {
  valido: 'Válido',
  a_expirar: 'A expirar',
  expirado: 'Expirado',
  sem_data: 'Sem validade',
};

export async function exportarFuncionariosExcel(lista: Funcionario[]) {
  const XLSX = await carregarXlsx();
  const dados = [
    COLUNAS_FUNCIONARIO as unknown as string[],
    ...lista.map((f) => [
      f.nome,
      f.empresa_nome ?? '',
      f.funcao_nome ?? f.funcao,
      f.data_nascimento ? dataCurta(f.data_nascimento, true) : '',
      f.email ?? '',
      f.telefone ?? '',
      f.cartao_id ?? '',
      f.cartao_validade ? dataCurta(f.cartao_validade, true) : '',
      ESTADO_CARTAO[f.cartao_estado ?? 'sem_data'] ?? '',
      f.nif ?? '',
      f.nib ?? '',
      f.data_admissao ? dataCurta(f.data_admissao, true) : '',
      f.custo_hora ?? '',
      f.ativo ? 'Ativo' : 'Inativo',
      f.notas ?? '',
    ]),
  ];

  const folha = XLSX.utils.aoa_to_sheet(dados);
  folha['!cols'] = [
    { wch: 26 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 28 }, { wch: 14 },
    { wch: 14 }, { wch: 11 }, { wch: 15 }, { wch: 12 }, { wch: 26 },
    { wch: 12 }, { wch: 11 }, { wch: 9 }, { wch: 30 },
  ];
  folha['!autofilter'] = { ref: `A1:O${lista.length + 1}` };
  folha['!freeze'] = { xSplit: 0, ySplit: 1 };

  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, folha, 'Funcionários');
  XLSX.writeFile(livro, `${nomeFicheiro('funcionarios', String(lista.length))}.xlsx`);
}

/*
 * The individual documents.
 *
 * These are what gets handed to a person or sent to an agency, so they follow
 * the layout the business already issues: who it is for, the shifts, then the
 * money worked out line by line — hours at their rate, km at theirs, and the
 * total that is actually owed. Anyone receiving one has to be able to check it
 * without asking a question.
 */


/** Header block shared by both documents. */
function cabecalhoDocumento(doc: InstanceType<typeof import('jspdf').default>, mes: string) {
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('FOLHA DE PONTO MENSAL', 14, 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90);
  doc.text(`Período: ${mesPorExtenso(mes)}`, 14, 25);
  doc.setTextColor(0);
  return 33;
}

/** Label/value pairs, one per line. */
function bloco(
  doc: InstanceType<typeof import('jspdf').default>,
  y: number, titulo: string, linhas: [string, string][],
) {
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo, 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  let cursor = y + 6;
  for (const [rotulo, valor] of linhas) {
    doc.text(`${rotulo}: ${valor}`, 14, cursor);
    cursor += 5.5;
  }
  return cursor + 2;
}

/**
 * The money block. Right-aligned against the page margin so the figures line
 * up as a column, with the payable total set apart from its components.
 */
function blocoTotais(
  doc: InstanceType<typeof import('jspdf').default>,
  y: number, linhas: [string, string][], total: [string, string],
) {
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const fim = largura - 14;

  // The block is placed after a table whose height is only known once drawn,
  // so it has to check that it fits. Without this it runs off the sheet: the
  // first two lines print over the footer and the total — the one figure the
  // document exists to state — is lost past the edge of the page.
  const necessario = linhas.length * 6 + 16;
  const limite = altura - 18;
  let cursor = y;
  if (cursor + necessario > limite) {
    doc.addPage();
    cursor = 24;
  }

  doc.setFontSize(10);
  for (const [rotulo, valor] of linhas) {
    doc.setFont('helvetica', 'normal');
    doc.text(rotulo, 14, cursor);
    doc.text(valor, fim, cursor, { align: 'right' });
    cursor += 6;
  }

  cursor += 1;
  doc.setDrawColor(...TINTA);
  doc.setLineWidth(0.4);
  doc.line(14, cursor, fim, cursor);
  cursor += 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(total[0], 14, cursor);
  doc.text(total[1], fim, cursor, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  return cursor + 8;
}

/** A rate that varied across the month is printed as a range, not a lie. */
const taxa = (min: number | null | undefined, max: number | null | undefined) =>
  min === null || min === undefined ? '—'
    : Math.abs((max ?? min) - min) < 0.005 ? euros(min)
      : `${euros(min)} – ${euros(max)}`;

export interface DocumentoFuncionario {
  mes: string;
  nome: string;
  funcao: string | null;
  empresa: string | null;
  registos: Registo[];
  totalHoras: number;
  totalKm: number;
  valorHoras: number;
  valorKm: number;
  total: number;
  valorHoraMin: number | null;
  valorHoraMax: number | null;
  valorKmUnitario: number | null;
}

export async function exportarDocumentoFuncionario(d: DocumentoFuncionario) {
  const { jsPDF, autoTable } = await carregarPdf();
  const doc = new jsPDF(A4);

  let y = cabecalhoDocumento(doc, d.mes);
  y = bloco(doc, y, 'DADOS DO FUNCIONÁRIO:', [
    ['Nome', d.nome],
    ['Função', d.funcao ?? '—'],
    ['Fornecedor', d.empresa ?? 'Sem fornecedor'],
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Data', 'Início', 'Fim', 'Total', 'Local', 'KM', 'Nº Serv.']],
    body: d.registos.map((r) => [
      dataCurta(r.data, true), r.hora_inicio, r.hora_fim, `${r.total_horas.toFixed(2)}h`,
      r.local_nome ?? '—', String(r.km), r.numero_servico ?? '—',
    ]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: TINTA, textColor: 255 },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: { 3: { halign: 'right' }, 5: { halign: 'right' } },
  });

  const depois = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  blocoTotais(doc, depois + 12, [
    ['Total de horas', `${d.totalHoras.toFixed(2)}h`],
    ['Total de km', `${d.totalKm} km`],
    ['Valor por hora', taxa(d.valorHoraMin, d.valorHoraMax)],
    ['Valor das horas', euros(d.valorHoras)],
    ['Valor por km', d.valorKmUnitario === null ? '—' : euros(d.valorKmUnitario)],
    ['Valor dos km', euros(d.valorKm)],
  ], ['Total a receber', euros(d.total)]);

  rodape(doc, `${d.nome} — Folha de Ponto`);
  doc.save(`${nomeFicheiro('folha-ponto', `${d.nome}-${d.mes}`)}.pdf`);
}

export interface LinhaDocumentoEmpresa {
  funcionario_nome: string;
  funcao_nome: string | null;
  n_dias: number;
  n_servicos: number;
  total_horas: number;
  total_km: number;
  valor_horas: number;
  valor_km: number;
  custo_total: number;
  valor_hora_min: number | null;
  valor_hora_max: number | null;
}

export interface DocumentoEmpresa {
  mes: string;
  empresa: string;
  tipo: string | null;
  porFuncionario: LinhaDocumentoEmpresa[];
  registos: Registo[];
  totalHoras: number;
  totalKm: number;
  valorHoras: number;
  valorKm: number;
  total: number;
  valorKmUnitario: number | null;
}

export async function exportarDocumentoEmpresa(d: DocumentoEmpresa) {
  const { jsPDF, autoTable } = await carregarPdf();
  const doc = new jsPDF(A4);

  let y = cabecalhoDocumento(doc, d.mes);
  y = bloco(doc, y, 'DADOS DO FORNECEDOR:', [
    ['Nome', d.empresa],
    ['Tipo', d.tipo === 'interno' ? 'Interno' : d.tipo === 'agencia' ? 'Agência' : '—'],
    ['Funcionários', String(d.porFuncionario.length)],
  ]);

  // Person by person first: this is the level the agency's invoice is checked
  // at, and it is what someone disputing a line looks for.
  autoTable(doc, {
    startY: y,
    head: [['Funcionário', 'Função', 'Dias', 'Horas', 'Km', '€/h', 'Valor horas', 'Valor km', 'Total']],
    body: d.porFuncionario.map((l) => [
      l.funcionario_nome, l.funcao_nome ?? '—', String(l.n_dias),
      `${l.total_horas.toFixed(2)}h`, String(l.total_km),
      taxa(l.valor_hora_min, l.valor_hora_max),
      euros(l.valor_horas), euros(l.valor_km), euros(l.custo_total),
    ]),
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 8 },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: {
      2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
      5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
      8: { halign: 'right', fontStyle: 'bold' },
    },
  });

  const depoisResumo = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  blocoTotais(doc, depoisResumo + 10, [
    ['Total de horas', `${d.totalHoras.toFixed(2)}h`],
    ['Total de km', `${d.totalKm} km`],
    ['Valor das horas', euros(d.valorHoras)],
    ['Valor por km', d.valorKmUnitario === null ? '—' : euros(d.valorKmUnitario)],
    ['Valor dos km', euros(d.valorKm)],
  ], ['Total a receber', euros(d.total)]);

  // The shift-by-shift detail backs it up, on its own page so the summary
  // above stays the first thing anyone reads.
  doc.addPage();
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`Detalhe — ${d.empresa}, ${mesPorExtenso(d.mes)}`, 14, 18);
  doc.setFont('helvetica', 'normal');

  autoTable(doc, {
    startY: 24,
    head: [['Data', 'Início', 'Fim', 'Total', 'Local', 'KM', 'Nº Serv.', 'Funcionário']],
    body: d.registos.map((r) => [
      dataCurta(r.data, true), r.hora_inicio, r.hora_fim, `${r.total_horas.toFixed(2)}h`,
      r.local_nome ?? '—', String(r.km), r.numero_servico ?? '—', r.funcionario_nome,
    ]),
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 8 },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: { 3: { halign: 'right' }, 5: { halign: 'right' } },
  });

  rodape(doc, `${d.empresa} — Folha de Ponto`);
  doc.save(`${nomeFicheiro('folha-ponto-empresa', `${d.empresa}-${d.mes}`)}.pdf`);
}

/* ===========================================================================
 * Monthly report — the dashboard as one A4 page.
 *
 * Drawn as vectors rather than screenshotted: a rasterised capture of the
 * screen would carry the browser's layout onto a page it was never designed
 * for, and print soft. This lays the same figures out for A4 and stays sharp
 * at any zoom, in a file of a few dozen kilobytes.
 * =========================================================================== */


/** Round an axis maximum up to a number a human would have chosen. */
function escalaAgradavel(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const base = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5]) {
    if (max <= m * base) return m * base;
  }
  return 10 * base;
}

/** A KPI tile: label, figure, and the comparison underneath. */
function cartaoKpi(
  doc: Doc, x: number, y: number, w: number, h: number,
  rotulo: string, valor: string, sub?: string, variacao?: number | null,
) {
  doc.setDrawColor(...GRELHA);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'S');

  doc.setFontSize(6);
  doc.setTextColor(...CINZA);
  doc.text(rotulo.toUpperCase(), x + 3.5, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TINTA);
  doc.text(valor, x + 3.5, y + 12.5);
  doc.setFont('helvetica', 'normal');

  const partes = [sub, variacao === null || variacao === undefined ? null : percentagem(variacao)]
    .filter(Boolean);
  if (partes.length) {
    doc.setFontSize(6);
    doc.setTextColor(...CINZA);
    doc.text(partes.join('  '), x + 3.5, y + 17);
  }
  doc.setTextColor(0);
}

/** Panel heading above a chart. Returns the y the plot should start at. */
function tituloPainel(doc: Doc, x: number, y: number, titulo: string, descricao?: string) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...TINTA);
  doc.text(titulo, x, y);
  doc.setFont('helvetica', 'normal');
  if (descricao) {
    doc.setFontSize(6.5);
    doc.setTextColor(...CINZA);
    doc.text(descricao, x, y + 4);
  }
  doc.setTextColor(0);
  return y + (descricao ? 9 : 6);
}

/** Horizontal gridlines with their value labels; returns the plot rectangle. */
function eixos(doc: Doc, x: number, y: number, w: number, h: number, max: number) {
  const margemEsq = 9;
  const alturaRotulos = 5;
  const px = x + margemEsq;
  const pw = w - margemEsq;
  const ph = h - alturaRotulos;

  doc.setFontSize(5.5);
  doc.setDrawColor(...GRELHA);
  doc.setLineWidth(0.2);
  for (let i = 0; i <= 2; i++) {
    const valor = (max / 2) * i;
    const gy = y + ph - (valor / max) * ph;
    doc.line(px, gy, px + pw, gy);
    doc.setTextColor(...CINZA);
    doc.text(numero(valor), px - 1.5, gy + 1.5, { align: 'right' });
  }
  doc.setTextColor(0);
  return { px, pw, ph };
}

function graficoBarras(
  doc: Doc, x: number, y: number, w: number, h: number,
  itens: { rotulo: string; valor: number }[],
) {
  if (!itens.length) return;
  const max = escalaAgradavel(Math.max(...itens.map((i) => i.valor)));
  const { px, pw, ph } = eixos(doc, x, y, w, h, max);

  const passo = pw / itens.length;
  const largura = Math.max(Math.min(passo * 0.62, 7), 0.8);
  doc.setFillColor(...MARCA);
  itens.forEach((item, i) => {
    const altura = (item.valor / max) * ph;
    if (altura <= 0) return;
    doc.rect(px + i * passo + (passo - largura) / 2, y + ph - altura, largura, altura, 'F');
  });

  // Label every nth bar: 31 days of labels at this width would overlap into
  // an unreadable smear.
  const cada = Math.ceil(itens.length / 8);
  doc.setFontSize(5.5);
  doc.setTextColor(...CINZA);
  itens.forEach((item, i) => {
    if (i % cada !== 0) return;
    doc.text(item.rotulo, px + i * passo + passo / 2, y + ph + 3.5, { align: 'center' });
  });
  doc.setTextColor(0);
}

function graficoLinha(
  doc: Doc, x: number, y: number, w: number, h: number,
  itens: { rotulo: string; valor: number }[],
) {
  if (!itens.length) return;
  const max = escalaAgradavel(Math.max(...itens.map((i) => i.valor)));
  const { px, pw, ph } = eixos(doc, x, y, w, h, max);

  const posX = (i: number) => (itens.length === 1
    ? px + pw / 2
    : px + (i / (itens.length - 1)) * pw);
  const posY = (v: number) => y + ph - (v / max) * ph;

  doc.setDrawColor(...MARCA);
  doc.setLineWidth(0.6);
  for (let i = 1; i < itens.length; i++) {
    doc.line(posX(i - 1), posY(itens[i - 1].valor), posX(i), posY(itens[i].valor));
  }
  doc.setFillColor(...MARCA);
  itens.forEach((item, i) => doc.circle(posX(i), posY(item.valor), 0.9, 'F'));

  doc.setFontSize(5.5);
  doc.setTextColor(...CINZA);
  const cada = Math.ceil(itens.length / 6);
  itens.forEach((item, i) => {
    if (i % cada !== 0 && i !== itens.length - 1) return;
    doc.text(item.rotulo, posX(i), y + ph + 3.5, { align: 'center' });
  });
  doc.setTextColor(0);
}

export async function exportarDashboardPdf(dados: DadosDashboard) {
  const { jsPDF, autoTable } = await carregarPdf();
  const doc = new jsPDF(A4);
  const { totais, variacao } = dados;

  // -- header ---------------------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...TINTA);
  doc.text('RELATÓRIO MENSAL', MARGEM, 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...CINZA);
  doc.text(mesPorExtenso(dados.mes), MARGEM, 24.5);
  doc.setFontSize(7);
  doc.text(`Gerado a ${new Date().toLocaleString('pt-PT')}`,
    210 - MARGEM, 24.5, { align: 'right' });
  doc.setDrawColor(...TINTA);
  doc.setLineWidth(0.5);
  doc.line(MARGEM, 28, 210 - MARGEM, 28);
  doc.setTextColor(0);

  // -- KPIs: three across, two down ----------------------------------------
  const larguraKpi = (LARGURA_UTIL - 8) / 3;
  const kpis: [string, string, string?, (number | null)?][] = [
    ['Total de horas', horas(totais.total_horas), `vs ${dados.mes_anterior}`, variacao.total_horas],
    ['Serviços', numero(totais.n_servicos), `${numero(totais.n_dias)} dias`, variacao.n_servicos],
    ['Funcionários', numero(totais.n_funcionarios), `${numero(totais.funcionarios_ativos)} ativos`],
    ['Horas extra', horas(totais.horas_extra),
      totais.total_horas
        ? `${((Number(totais.horas_extra) / totais.total_horas) * 100).toFixed(1)}% do total`
        : undefined],
    ['Quilómetros', km(totais.total_km)],
    ['Custo estimado', euros(totais.custo_total), `vs ${dados.mes_anterior}`, variacao.custo_total],
  ];
  kpis.forEach(([rotulo, valor, sub, varia], i) => {
    const x = MARGEM + (i % 3) * (larguraKpi + 4);
    const y = 34 + Math.floor(i / 3) * 24;
    cartaoKpi(doc, x, y, larguraKpi, 20, rotulo, valor, sub, varia);
  });

  // -- charts, side by side -------------------------------------------------
  const larguraGrafico = (LARGURA_UTIL - 6) / 2;
  const xDireita = MARGEM + larguraGrafico + 6;

  let yPlot = tituloPainel(doc, MARGEM, 88, 'Horas por dia',
    `Distribuição em ${mesPorExtenso(dados.mes)}`);
  graficoBarras(doc, MARGEM, yPlot, larguraGrafico, 42,
    dados.por_dia.map((d) => ({ rotulo: dataCurta(d.data), valor: d.total_horas })));

  tituloPainel(doc, xDireita, 88, 'Evolução de horas', 'Últimos meses com registos');
  graficoLinha(doc, xDireita, yPlot, larguraGrafico, 42,
    dados.evolucao.map((e) => ({ rotulo: e.mes.slice(2).replace('-', '/'), valor: e.total_horas })));

  // -- the two rankings, side by side --------------------------------------
  //
  // Funcionários is a top-5 on purpose: on a busy month it runs to seventy
  // people and would bury everything under it, and the folha mensal already
  // lists them all. Locais is shown in full — it is a handful of venues, and
  // "where did the month happen" is not a question a truncated list answers.
  const TOPO_FUNCIONARIOS = 5;
  const yTabelas = yPlot + 52;
  tituloPainel(doc, MARGEM, yTabelas, 'Horas por funcionário',
    `${dados.por_funcionario.length} pessoa(s) neste mês`);
  tituloPainel(doc, xDireita, yTabelas, 'Horas por local', 'Onde o trabalho aconteceu');

  const estiloTabela = {
    startY: yTabelas + 9,
    styles: { fontSize: 7, cellPadding: 1.2 },
    headStyles: { fillColor: TINTA, textColor: 255, fontSize: 6.5 },
    alternateRowStyles: { fillColor: PAPEL },
    columnStyles: { 1: { halign: 'right' as const }, 2: { halign: 'right' as const } },
  };

  autoTable(doc, {
    ...estiloTabela,
    head: [['Funcionário', 'Horas', 'Serv.']],
    body: dados.por_funcionario.slice(0, TOPO_FUNCIONARIOS).map((f) => [
      f.funcionario_nome, horas(f.total_horas), String(f.n_servicos),
    ]),
    margin: { left: MARGEM, right: 210 - MARGEM - larguraGrafico },
    tableWidth: larguraGrafico,
  });
  const fimEsquerda = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  autoTable(doc, {
    ...estiloTabela,
    head: [['Local', 'Horas', 'Serv.']],
    body: dados.por_local.map((l) => [
      l.local_nome, horas(l.total_horas), String(l.n_servicos),
    ]),
    margin: { left: xDireita, right: MARGEM },
    tableWidth: larguraGrafico,
  });
  const fimDireita = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  // Truncating without saying so would read as "that is everyone".
  let y = Math.max(fimEsquerda, fimDireita) + 4;
  const cortados = Math.max(dados.por_funcionario.length - TOPO_FUNCIONARIOS, 0);
  if (cortados > 0) {
    doc.setFontSize(6.5);
    doc.setTextColor(...CINZA);
    doc.text(
      `Mostradas as ${TOPO_FUNCIONARIOS} com mais horas, de ${dados.por_funcionario.length} pessoas — `
      + 'a folha mensal tem a lista completa.',
      MARGEM, y,
    );
    doc.setTextColor(0);
    y += 5;
  }

  // -- every serviço of the month, complete ---------------------------------
  //
  // Never truncated. This is the list the month is checked against, and one
  // missing job is a job nobody notices is missing — the earlier version fitted
  // it to the space left on page one and silently dropped two of August's
  // seven. It runs onto as many pages as it needs; autoTable repeats the header
  // on each.
  if (dados.servicos_do_mes.length) {
    const somaHoras = Math.round(
      dados.servicos_do_mes.reduce((a, s) => a + s.total_horas, 0) * 100) / 100;
    const somaCusto = Math.round(
      dados.servicos_do_mes.reduce((a, s) => a + (s.custo_total ?? 0), 0) * 100) / 100;

    tituloPainel(doc, MARGEM, y + 4, 'Serviços do mês',
      `${dados.servicos_do_mes.length} serviço(s), do mais recente`);
    autoTable(doc, {
      startY: y + 13,
      head: [['Nº Serviço', 'Data', 'Local', 'Funcionários', 'Horas', 'Custo']],
      body: dados.servicos_do_mes.map((s) => [
        s.numero, dataCurta(s.data, true), s.local_nome ?? '—',
        String(s.n_funcionarios), horas(s.total_horas), euros(s.custo_total),
      ]),
      foot: [[
        'TOTAL', '', '', '', horas(somaHoras), euros(somaCusto),
      ]],
      styles: { fontSize: 7, cellPadding: 1.2 },
      headStyles: { fillColor: TINTA, textColor: 255, fontSize: 6.5 },
      footStyles: { fillColor: PAPEL, textColor: 20, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: PAPEL },
      columnStyles: {
        3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' },
      },
      margin: { left: MARGEM, right: MARGEM, bottom: 18 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

    // Shifts with no serviço number belong to no serviço, so they are absent
    // from this table and its total lands under the month's. Unexplained, that
    // reads as an arithmetic error in the report rather than a gap in the data.
    const foraHoras = Math.round((dados.totais.total_horas - somaHoras) * 100) / 100;
    if (Math.abs(foraHoras) > 0.005) {
      const foraCusto = Math.round(((dados.totais.custo_total ?? 0) - somaCusto) * 100) / 100;
      doc.setFontSize(6.5);
      doc.setTextColor(...CINZA);
      doc.text(
        `Fora desta tabela: ${horas(foraHoras)} e ${euros(foraCusto)} em registos sem nº de serviço `
        + '— contam no total do mês acima, mas não pertencem a nenhum serviço.',
        MARGEM, y,
      );
      doc.setTextColor(0);
      y += 5;
    }
  }

  // -- anything the month needs someone to look at --------------------------
  if (dados.alertas.length) {
    // Same rule as the totals block: measure before placing, or the last
    // thing on the page is the thing that gets lost.
    const preciso = 12 + dados.alertas.length * 8;
    if (y + preciso > 279) {
      doc.addPage();
      y = 18;
    }
    y = tituloPainel(doc, MARGEM, y + 4, 'A confirmar');
    doc.setFontSize(7.5);
    for (const alerta of dados.alertas) {
      const linhas = doc.splitTextToSize(`•  ${alerta.mensagem}`, LARGURA_UTIL) as string[];
      doc.text(linhas, MARGEM, y);
      y += linhas.length * 3.6 + 1.5;
    }
  }

  rodape(doc, 'Relatório Mensal');

  doc.save(`${nomeFicheiro('relatorio-mensal', dados.mes)}.pdf`);
}

const COLUNAS_FOLHA = [
  'Funcionário', 'Função', 'Fornecedor', 'Dias', 'Serviços', 'Total horas',
  'Km', 'Valor horas', 'Valor km', 'A receber',
] as const;

const linhaFolha = (l: LinhaFolha) => [
  l.funcionario_nome, l.funcao_nome ?? '', l.empresa_nome ?? '', l.n_dias, l.n_servicos,
  l.total_horas, l.total_km, l.valor_horas ?? '', l.valor_km ?? '', l.custo_total ?? '',
];

export async function exportarFolhaExcel(linhas: LinhaFolha[], mes: string) {
  const XLSX = await carregarXlsx();
  const soma = (campo: keyof LinhaFolha) =>
    Math.round(linhas.reduce((acc, l) => acc + (Number(l[campo]) || 0), 0) * 100) / 100;

  const dados = [
    [`Folha Mensal — ${mesPorExtenso(mes)}`],
    [],
    COLUNAS_FOLHA as unknown as string[],
    ...linhas.map(linhaFolha),
    [],
    ['TOTAL', '', '', soma('n_dias'), soma('n_servicos'), soma('total_horas'),
      soma('total_km'), soma('valor_horas'), soma('valor_km'), soma('custo_total')],
  ];

  const folha = XLSX.utils.aoa_to_sheet(dados);
  folha['!cols'] = [
    { wch: 26 }, { wch: 18 }, { wch: 16 }, { wch: 7 }, { wch: 10 },
    { wch: 12 }, { wch: 9 }, { wch: 12 }, { wch: 11 }, { wch: 12 },
  ];

  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, folha, 'Folha Mensal');
  XLSX.writeFile(livro, `${nomeFicheiro('folha-mensal', mes)}.xlsx`);
}

export async function exportarFolhaPdf(linhas: LinhaFolha[], mes: string) {
  const { jsPDF, autoTable } = await carregarPdf();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  doc.setFontSize(14);
  doc.text('Folha Mensal', 14, 15);
  doc.setFontSize(10);
  doc.setTextColor(110);
  doc.text(mesPorExtenso(mes), 14, 21);

  const soma = (campo: keyof LinhaFolha) =>
    Math.round(linhas.reduce((acc, l) => acc + (Number(l[campo]) || 0), 0) * 100) / 100;

  autoTable(doc, {
    startY: 27,
    head: [COLUNAS_FOLHA as unknown as string[]],
    // The last three columns are money; the rest print as they come.
    body: linhas.map((l) => linhaFolha(l).map((v, i) =>
      i >= 7 && v !== '' ? euros(Number(v)) : String(v))),
    foot: [[
      'TOTAL', '', '', String(soma('n_dias')), String(soma('n_servicos')),
      String(soma('total_horas')), String(soma('total_km')),
      euros(soma('valor_horas')), euros(soma('valor_km')), euros(soma('custo_total')),
    ]],
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: TINTA, textColor: 255 },
    footStyles: { fillColor: PAPEL, textColor: 20, fontStyle: 'bold' },
    columnStyles: {
      3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' },
      6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' },
      9: { halign: 'right', fontStyle: 'bold' },
    },
  });

  rodape(doc, 'Folha Mensal');
  doc.save(`${nomeFicheiro('folha-mensal', mes)}.pdf`);
}
